import React, { Fragment, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from 'react-router-dom';
// import Flatpickr from 'react-flatpickr';
import BreadCrumb from "Common/BreadCrumb";
import Select from 'react-select';

// Icon
import { UploadCloud } from 'lucide-react';

// Image
import Dropzone from "react-dropzone";

import * as Yup from "yup";
import { FieldArray, FormikProvider, useFormik } from "formik";
import { useDispatch } from "react-redux";
import { addProductList, updateProductList } from "slices/thunk";
import { ToastContainer } from "react-toastify";
import { currencyFormatter } from "helpers/formatter";
import deleteImg from "assets/images/delete.png";
import addImg from "assets/images/add.png";
import { getS3UrlFromFileId, toBase64 } from "../../../helpers/file_helper";
import { doGetProductColors, doListProductCategories } from "../../../services/product.service";
import { doFileUpload } from "../../../services/file.service";

const AddNew = () => {
  const { pathname, state } = useLocation();
  const navigate = useNavigate();
  const [action] = useState(pathname === '/apps-ecommerce-product-update' ? 'update' : 'add');
  const isUpdate = action === 'update';

  const variantsSectionRef = document.getElementById('variants-section')

  const dispatch = useDispatch<any>();

  const [selectfiles, setSelectfiles] = useState<Array<any>>([]);
  const [data, setData] = useState<any>();

  const [options, setOptions] = useState<Array<{ value: string, label: string }>>([])
  const [availableColors, setAvailableColors] = useState<Array<{ value: string, label: string }>>([])

  const handleAcceptfiles = async (files: any) => {
    const results = []
    for (const file of files) {
      const fileType = file.name.split('.').pop()
      results.push(Object.assign(file, {
        preview: URL.createObjectURL(file),
        formattedSize: formatBytes(file.size),
        base64: await toBase64(file),
        filename: new Date().valueOf() + '.' + fileType,
      }));
    }
    setSelectfiles(results);
  };

  const formatBytes = (bytes: any, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  const statusSelect = [
    { value: '', label: 'Select Status' },
    { value: 'active', label: 'Active' },
    { value: 'out of stock', label: 'Out of Stock' },
    { value: "hidden", label: "Hidden" },
  ];

  const configType = [
    { value: 'input', label: 'Input' },
    { value: 'number', label: 'Number' },
    { value: 'choices', label: 'Multiple Choices' },
    { value: 'color', label: 'Color' },
  ];

  const validation: any = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      productId: (data && data.productId) || null,
      images: (data && data.images) || [],
      files: (data && data.files) || [],
      name: (data && data.name) || '',
      code: (data && data.code) || '',
      specification: (data && data.specification) || [{ key: '', value: '' }],
      subtitle: (data && data.subtitle) || '',
      price: (data && data.price) || '',
      wholeSalePrice: (data && data.wholeSalePrice) || '',
      priceUnit: (data && data.priceUnit) || '',
      discount: (data && data.discount) || '',
      fixedFee: (data && data.fixedFee) || 0,
      stock: (data && data.stock) || '',
      description: (data && data.description) || '',
      categoryId: (data && data.categoryId) || '',
      status: (data && data.status) || '',
      configs: (data && data.configs) || [],
    },
    validationSchema: Yup.object({
      productId: Yup.string().optional().nullable(),
      images: Yup.array().optional().nullable(),
      name: Yup.string().required("Please Enter Name"),
      code: Yup.string().required("Please Enter Code"),
      specification: Yup.array(
        Yup.object().shape({
          key: Yup.string(),
          value: Yup.string(),
        })
      ),
      subtitle: Yup.string(),
      price: Yup.number().required("Please Enter Price"),
      wholeSalePrice: Yup.number().optional(),
      priceUnit: Yup.string().optional(),
      discount: Yup.number().optional(),
      fixedFee: Yup.number().default(0),
      stock: Yup.number().optional(),
      description: Yup.string(),
      categoryId: Yup.string().required("Please Enter Category"),
      status: Yup.string().required("Please Enter Status"),
      configs: Yup.array(
        Yup.object().shape({
          productConfigId: Yup.string().optional().nullable(),
          key: Yup.string().required("Please Enter Key"),
          type: Yup.string(),
          isMultiplyPrice: Yup.boolean().default(false),
          price: Yup.number().optional().nullable(),
          defaultValue: Yup.string().when('isMultiplyPrice', {
            is: true,
            then: (schema: any) => schema.required("Please Enter Default Value"),
            otherwise: (schema: any) => schema.optional().nullable()
          }),
          instruction: Yup.string().optional().nullable(),
          details: Yup.array(
            Yup.object().shape({
              productConfigDetailId: Yup.string().optional().nullable(),
              value: Yup.string(),
              stock: Yup.number().optional().nullable(),
              image: Yup.string().optional().nullable(),
              price: Yup.number().optional().nullable(),
            }).optional(),
          ).optional(),
        })
      ).optional(),
    }),

    onSubmit: async (values) => {
      const images = []
      if (selectfiles.length > 0) {
        for (const file of selectfiles) {
          const fileObject = await doFileUpload(file.name, file.base64)
          images.push(fileObject.fileId)
        }
      }

      const data = {
        ...values,
        images: [...values.images, ...images],
      };

      if (data.productId) {
        dispatch(updateProductList(data));
      } else {
        delete data.productId;
        dispatch(addProductList(data));
        validation.resetForm();
      }

      navigate("/apps-ecommerce-product-list");
    },
  });

  const getProductCategories = async () => {
    doListProductCategories(0).then((response: any) => {
      const productCategories = response?.data.map((category: any) => {
        return { value: category.productCategoryId, label: category.categoryName }
      })
      setOptions(productCategories)
    })
  }

  const getProductColors = async () => {
    doGetProductColors().then((response: any) => {
      const colors = response?.data.map((color: any) => {
        return { value: color.code, label: color.value }
      })
      setAvailableColors(colors)
    })
  }

  useEffect(() => {
    getProductCategories()
    getProductColors()
    if (isUpdate) {
      setData(state)
    } else {
      setSelectfiles([]);
      validation.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <React.Fragment>
      <BreadCrumb title={isUpdate ? `Product - ${validation.values.name}` : 'Add New Product'} pageTitle='Products' pageTitleLink="/apps-ecommerce-product-list" />
      <ToastContainer closeButton={false} limit={1} />
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-x-5">
        <div className="xl:col-span-9">
          <div className="card scroll">
            <div id="card-body" className="!pb-32 card-body">
              <h6 className="mb-4 text-15">{isUpdate ? 'Update Product' : 'Create Product'}</h6>

              <FormikProvider value={validation}>
                <form action="#!" onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}>
                  <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12">
                    <div className="xl:col-span-6">
                      <label htmlFor="productNameInput" className="inline-block mb-2 text-base font-medium">Product Name</label>
                      <input type="text" id="productNameInput" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="Product Name"
                        name="name"
                        onChange={validation.handleChange}
                        value={validation.values.name || ""} />
                      {validation.touched.name && validation.errors.name ? (
                        <p className="text-red-400">{validation.errors.name}</p>
                      ) : null}
                      <p className="mt-1 text-sm text-slate-400 dark:text-zink-200">Do not exceed 20 characters when entering the product name.</p>
                    </div>
                    <div className="xl:col-span-6">
                      <label htmlFor="productCodeInput" className="inline-block mb-2 text-base font-medium">Product Code</label>
                      <input type="text" id="productCodeInput" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="Product Code"
                        name="code"
                        onChange={validation.handleChange}
                        value={validation.values.code || ""} />
                      {validation.touched.code && validation.errors.code ? (
                        <p className="text-red-400">{validation.errors.code}</p>
                      ) : null}
                      <p className="mt-1 text-sm text-slate-400 dark:text-zink-200">Code will be generated automatically</p>
                    </div>
                    <div className="xl:col-span-4">
                      <label htmlFor="quantityInput" className="inline-block mb-2 text-base font-medium">Stock</label>
                      <input type="number" id="quantityInput" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="Quantity"
                        name="stock"
                        onChange={validation.handleChange}
                        value={validation.values.stock || ""} />
                      {validation.touched.stock && validation.errors.stock ? (
                        <p className="text-red-400">{validation.errors.stock}</p>
                      ) : null}
                    </div>
                    <div className="xl:col-span-4">
                      <label htmlFor="categorySelect" className="inline-block mb-2 text-base font-medium">Category</label>
                      <Select
                        className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                        options={options}
                        isSearchable={false} // To disable search
                        name="categoryId"
                        id="categorySelect"
                        onChange={selectedOption => {
                          validation.setFieldValue(`categoryId`, selectedOption?.value)
                        }}
                        value={options.find(option => { return option.value === validation.values.categoryId })}
                      />
                      {validation.touched.category && validation.errors.category ? (
                        <p className="text-red-400">{validation.errors.category}</p>
                      ) : null}
                    </div>
                    <div className="xl:col-span-12">
                      <label htmlFor="productSpecification" className="inline-block mb-2 text-base font-medium">Specification</label>
                      <div>
                        <FieldArray name="specification" render={arrayHelpers => (
                          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12">
                            {
                              validation.values.specification?.map((spec: any, index: number) => {
                                return (
                                  <Fragment key={index}>
                                    <div className="xl:col-span-4">
                                      <input type="text" id={`specKeyInput[${index}]`} className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                        placeholder="Key"
                                        name={`specification[${index}]key`}
                                        onChange={validation.handleChange}
                                        value={validation.values.specification[index].key || ""} />
                                      {validation.touched.specification?.[index]?.key && validation.errors.specification?.[index]?.key ? (
                                        <p className="text-red-400">{validation.errors.specification?.[index]?.key}</p>
                                      ) : null}
                                    </div>
                                    <div className="xl:col-span-4">
                                      <input type="text" id={`specValueInput[${index}]`} className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                        placeholder="Value"
                                        name={`specification[${index}]value`}
                                        onChange={validation.handleChange}
                                        value={validation.values.specification[index].value || ""} />
                                      {validation.touched.specification?.[index]?.value && validation.errors.specification?.[index]?.value ? (
                                        <p className="text-red-400">{validation.errors.specification?.[index]?.value}</p>
                                      ) : null}
                                    </div>
                                    <div className="xl:col-span-4">
                                      {
                                        index > 0 &&
                                        <button type="button" className="btn btn-danger" onClick={() => {
                                          const newSpec = [...validation.values.specification];
                                          newSpec.splice(index, 1);
                                          validation.setFieldValue('specification', newSpec);
                                        }}><img src={deleteImg} alt="" className="block h-5 mx-auto" /></button>
                                      }
                                      {
                                        index === validation.values.specification?.length - 1 &&
                                        <button type="button" className="btn btn-primary" onClick={() => {
                                          const newSpec = [...validation.values.specification, { key: '', value: '' }];
                                          validation.setFieldValue('specification', newSpec);

                                        }}><img src={addImg} alt="" className="block h-5 mx-auto" /></button>
                                      }

                                    </div>
                                  </Fragment>
                                )
                              })}
                          </div>
                        )}
                        />
                      </div>
                    </div>
                    <div className="lg:col-span-2 xl:col-span-12">
                      <label htmlFor="productImages" className="inline-block mb-2 text-base font-medium">Product Images</label>

                      <Dropzone
                        accept={{ "image/*": ['.png', '.jpg', '.jpeg'] }}
                        onDrop={(acceptfiles: any) => {
                          handleAcceptfiles(acceptfiles);
                        }}>
                        {({ getRootProps }: any) => (
                          <div className="flex items-center justify-center bg-white border border-dashed rounded-md cursor-pointer dropzone border-slate-300 dark:bg-zink-700 dark:border-zink-500 dropzone2">
                            <div className="w-full py-5 text-lg text-center dz-message needsclick" {...getRootProps()} >
                              <div className="mb-3">
                                <UploadCloud className="block w-12 h-12 mx-auto text-slate-500 fill-slate-200 dark:text-zink-200 dark:fill-zink-500" />
                              </div>
                              <h5 className="mb-0 font-normal text-slate-500 dark:text-zink-200 text-15">Drag and drop your product images or <Link to="#!">browse</Link> your product images</h5>
                            </div>
                          </div>
                        )}
                      </Dropzone>
                      <ul className="flex flex-wrap mb-0 gap-x-5" id="dropzone-preview2">
                        {
                          (validation.values.images || [])?.map((fileId: string, index: number) => {
                            return (
                              <li className="mt-5" id="dropzone-preview-list2" key={index}>
                                <div className="border rounded border-slate-200 dark:border-zink-500">
                                  <div className="p-2 text-center">
                                    <div>
                                      <div className="p-2 mx-auto rounded-md w-14 h-14 bg-slate-100 dark:bg-zink-600">
                                        <img className="block w-full h-full rounded-md" src={getS3UrlFromFileId(fileId, validation.values.files)} alt={fileId} />
                                      </div>
                                    </div>
                                    <div className="mt-2">
                                      <button data-dz-remove className="px-2 py-1.5 text-xs text-white bg-red-500 border-red-500 btn hover:text-white hover:bg-red-600 hover:border-red-600 focus:text-white focus:bg-red-600 focus:border-red-600 focus:ring focus:ring-red-100 active:text-white active:bg-red-600 active:border-red-600 active:ring active:ring-red-100 dark:ring-custom-400/20" onClick={() => {
                                        const newImages = [...validation.values.images];
                                        newImages.splice(index, 1);
                                        validation.setFieldValue('images', newImages);
                                      }}>Delete</button>
                                    </div>
                                  </div>
                                </div>
                              </li>
                            );
                          })
                        }
                        {
                          (selectfiles || [])?.map((file: any, index: number) => {
                            return (
                              <li className="mt-5" id="dropzone-preview-list2" key={index}>
                                <div className="border rounded border-slate-200 dark:border-zink-500">
                                  <div className="p-2 text-center">
                                    <div>
                                      <div className="p-2 mx-auto rounded-md w-14 h-14 bg-slate-100 dark:bg-zink-600">
                                        <img className="block w-full h-full rounded-md" src={file.preview} alt={file.name} />
                                      </div>
                                    </div>
                                    <div className="pt-3">
                                      <h5 className="mb-1 text-15" data-dz-name>{file.path}</h5>
                                      <p className="mb-0 text-slate-500 dark:text-zink-200" data-dz-size>{file.formattedSize}</p>
                                      <strong className="error text-danger" data-dz-errormessage></strong>
                                    </div>
                                    <div className="mt-2">
                                      <button data-dz-remove className="px-2 py-1.5 text-xs text-white bg-red-500 border-red-500 btn hover:text-white hover:bg-red-600 hover:border-red-600 focus:text-white focus:bg-red-600 focus:border-red-600 focus:ring focus:ring-red-100 active:text-white active:bg-red-600 active:border-red-600 active:ring active:ring-red-100 dark:ring-custom-400/20" onClick={() => {
                                        const newImages = [...selectfiles];
                                        newImages.splice(index, 1);
                                        setSelectfiles(newImages);
                                      }}>Delete</button>
                                    </div>
                                  </div>
                                </div>
                              </li>
                            );
                          })
                        }
                      </ul>
                    </div>
                    <div className="lg:col-span-2 xl:col-span-12">
                      <div>
                        <label htmlFor="productDescription" className="inline-block mb-2 text-base font-medium">Description</label>
                        <textarea className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" id="productDescription" placeholder="Enter Description" rows={5}
                          name="description"
                          onChange={validation.handleChange}
                          value={validation.values.description || ""}></textarea>
                        {validation.touched.description && validation.errors.description ? (
                          <p className="text-red-400">{validation.errors.description}</p>
                        ) : null}
                      </div>
                    </div>
                    <div className="xl:col-span-4">
                      <label htmlFor="productPrice" className="inline-block mb-2 text-base font-medium">Price (Retail)</label>
                      <input type="number" id="productPrice" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="$0.00"
                        name="price"
                        onChange={validation.handleChange}
                        value={validation.values.price || ""} />
                      {validation.touched.price && validation.errors.price ? (
                        <p className="text-red-400">{validation.errors.price}</p>
                      ) : null}
                    </div>
                    <div className="xl:col-span-4">
                      <label htmlFor="productWholeSalePrice" className="inline-block mb-2 text-base font-medium">Price (Wholesale)</label>
                      <input type="number" id="productWholeSalePrice" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="$0.00"
                        name="wholeSalePrice"
                        onChange={validation.handleChange}
                        value={validation.values.wholeSalePrice || ""} />
                      {validation.touched.wholeSalePrice && validation.errors.wholeSalePrice ? (
                        <p className="text-red-400">{validation.errors.wholeSalePrice}</p>
                      ) : null}
                    </div>
                    <div className="xl:col-span-4">
                      <label htmlFor="productPriceUnit" className="inline-block mb-2 text-base font-medium">Unit</label>
                      <input type="text" id="productPriceUnit" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                        placeholder="Unit"
                        name="priceUnit"
                        onChange={validation.handleChange}
                        value={validation.values.priceUnit || ""} />
                      {validation.touched.priceUnit && validation.errors.priceUnit ? (
                        <p className="text-red-400">{validation.errors.priceUnit}</p>
                      ) : null}
                    </div>
                    {/* <div className="xl:col-span-4">
                      <label htmlFor="publishDateTime" className="inline-block mb-2 text-base font-medium">Publish Date & Time</label>
                      <Flatpickr
                        id="publishDateTime"
                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                        options={{
                          dateFormat: "d M, Y",
                          enableTime: true,
                        }}
                        placeholder='Select date & time'
                      />
                    </div> */}
                    <div className="xl:col-span-4">
                      <label htmlFor="productStatus" className="inline-block mb-2 text-base font-medium">Status</label>
                      <Select
                        className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                        options={statusSelect}
                        isSearchable={false} // To disable search
                        name="status"
                        id="statusSelect"
                        onChange={selectedOption => {
                          validation.setFieldValue(`status`, selectedOption?.value)
                        }}
                        value={statusSelect.find(type => { return type.value === validation.values.status })}
                      />
                      {validation.touched.status && validation.errors.status ? (
                        <p className="text-red-400">{validation.errors.status}</p>
                      ) : null}
                    </div>
                    <div className="xl:col-span-4">
                      <label htmlFor="productDiscount" className="inline-block mb-2 text-base font-medium">Discount(%)</label>
                      <input type="number" id="productDiscount" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                        placeholder="0.00"
                        name="discount"
                        onChange={validation.handleChange}
                        value={validation.values.discount || ""} />
                      {validation.touched.discount && validation.errors.discount ? (
                        <p className="text-red-400">{validation.errors.discount}</p>
                      ) : null}
                    </div>
                    <div className="xl:col-span-4">
                      <label htmlFor="productFixedFee" className="inline-block mb-2 text-base font-medium">Fixed Fee</label>
                      <input type="number" id="productFixedFee" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="$0.00"
                        name="fixedFee"
                        onChange={validation.handleChange}
                        value={validation.values.fixedFee || ""} />
                      {validation.touched.fixedFee && validation.errors.fixedFee ? (
                        <p className="text-red-400">{validation.errors.fixedFee}</p>
                      ) : null}
                    </div>
                    <div className="xl:col-span-12">
                      <label className="inline-block mb-2 text-15 font-medium">Variants</label>
                      {
                        <button type="button" className="btn btn-primary" onClick={() => {
                          const newConfig = [...validation.values.configs, {
                            key: '', config_type: 'input', details: [{ value: '', stock: '', price: '', image: '' }], level: 0
                          }];
                          validation.setFieldValue('configs', newConfig);
                          variantsSectionRef?.scrollIntoView({ behavior: "smooth", block: "end" });

                        }}><img src={addImg} alt="" className="block h-3 mx-auto" /></button>
                      }
                    </div>
                    <div style={{ scrollMarginBottom: "1000px" }} id="variants-section" className="xl:col-span-12">
                      <FieldArray name="configs" render={arrayHelpers => (
                        <div>
                          {
                            validation.values.configs?.map((config: { key: string, type: string, details: Array<Record<string, unknown>>, level: number }, configIndex: number) => {
                              return (
                                <Fragment key={configIndex}>
                                  <label className={(configIndex > 0 ? "mt-5" : "") + " inline-block mb-2 text-base font-medium"}>Variant {configIndex + 1}</label>
                                  <div className={"grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12"}>
                                    <div className="xl:col-span-4">
                                      <input type="text" id={`configKeyInput[${configIndex}]`} className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                        placeholder="Name"
                                        name={`configs[${configIndex}]key`}
                                        onChange={validation.handleChange}
                                        value={validation.values.configs[configIndex].key || ""} />
                                      {validation.touched.configs?.[configIndex]?.key && validation.errors.configs?.[configIndex]?.key ? (
                                        <p className="text-red-400">{validation.errors.configs?.[configIndex]?.key}</p>
                                      ) : null}
                                    </div>
                                    <div className="xl:col-span-4">
                                      <Select
                                        className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                        options={configType}
                                        isSearchable={false} // To disable search
                                        name={`configs[${configIndex}].type`}
                                        id={`typeInput[${configIndex}]`}
                                        onChange={selectedOption => {
                                          if (!validation.values.configs[configIndex].details.length) {
                                            validation.setFieldValue(`configs[${configIndex}].details`, [{ value: '', stock: '', price: '', image: '' }]);
                                          }
                                          validation.setFieldValue(`configs[${configIndex}].type`, selectedOption?.value)
                                        }}
                                        value={configType.find(type => { return type.value === validation.values.configs[configIndex].type })}
                                      />
                                      {validation.touched.configs?.[configIndex]?.type && validation.errors.configs?.[configIndex]?.type ? (
                                        <p className="text-red-400">{validation.errors.configs?.[configIndex]?.type}</p>
                                      ) : null}
                                    </div>
                                    <div className="xl:col-span-4">
                                      {
                                        <button type="button" className="btn btn-danger" onClick={() => {
                                          const newConfig = [...validation.values.configs];
                                          newConfig.splice(configIndex, 1);
                                          validation.setFieldValue('configs', newConfig);
                                        }}><img src={deleteImg} alt="" className="block h-5 mx-auto" /></button>
                                      }
                                    </div>
                                    {
                                      validation.values.configs[configIndex].type === 'number' &&
                                      <div className="xl:col-span-12">
                                        <FieldArray name="configDetails" render={configDetailsArray => (
                                          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12">
                                            {/* <div className="xl:col-span-4">
                                              <input type="number" id={`configDetailValue[${configIndex}].price`}
                                                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                                placeholder="Custom Price"
                                                name={`configs[${configIndex}].price`}
                                                onChange={validation.handleChange}
                                                value={validation.values.configs[configIndex].price || ""} />
                                              {validation.touched.price && validation.errors.price ? (
                                                <p className="text-red-400">{validation.errors.price}</p>
                                              ) : null}
                                            </div> */}
                                            <div className="xl:col-span-4">
                                              <input type="number" id={`configDetailValue[${configIndex}].defaultValue`}
                                                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                                placeholder="Default Value"
                                                name={`configs[${configIndex}].defaultValue`}
                                                onChange={validation.handleChange}
                                                value={validation.values.configs[configIndex]?.defaultValue || ""} />
                                              {validation.touched.configs?.[configIndex]?.defaultValue && validation.errors.configs?.[configIndex]?.defaultValue ? (
                                                <p className="text-red-400">{validation.errors.configs?.[configIndex]?.defaultValue}</p>
                                              ) : null}
                                            </div>
                                            <div className="xl:col-span-4">
                                              <div className="flex items-center h-full">
                                                <input type="checkbox" id={`configDetailValue[${configIndex}].isMultiplyPrice`}
                                                  className="w-10 form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                                  name={`configs[${configIndex}].isMultiplyPrice`}
                                                  onChange={validation.handleChange}
                                                  checked={validation.values.configs[configIndex].isMultiplyPrice}
                                                  value="true" />
                                                <label>Multiply with Price</label>
                                                {validation.touched.configs?.[configIndex]?.isMultiplyPrice && validation.errors.configs?.[configIndex]?.isMultiplyPrice ? (
                                                  <p className="text-red-400">{validation.errors.configs?.[configIndex]?.isMultiplyPrice}</p>
                                                ) : null}
                                              </div>
                                            </div>
                                            <div className="xl:col-span-8">
                                              <input type="text" id={`configDetailValue[${configIndex}].instruction`}
                                                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                                placeholder="Instruction"
                                                name={`configs[${configIndex}].instruction`}
                                                onChange={validation.handleChange}
                                                value={validation.values.configs[configIndex].instruction || ""} />
                                              {validation.touched.configs?.[configIndex]?.instruction && validation.errors.configs?.[configIndex]?.instruction ? (
                                                <p className="text-red-400">{validation.errors.configs?.[configIndex]?.instruction}</p>
                                              ) : null}
                                            </div>
                                          </div>
                                        )} />
                                      </div>
                                    }
                                    {
                                      validation.values.configs[configIndex].type === 'choices' &&
                                      <div className="xl:col-span-12">
                                        <FieldArray name="configDetails" render={configDetailsArray => (
                                          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12">
                                            {
                                              config.details.map((detail: any, detailIndex: number) => {
                                                return (
                                                  <Fragment key={detailIndex}>
                                                    <div className="xl:col-span-3">
                                                      <input type="text" id={`configDetailValue[${configIndex}][${detailIndex}]`} className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                                        placeholder="Choice"
                                                        name={`configs[${configIndex}].details[${detailIndex}].value`}
                                                        onChange={validation.handleChange}
                                                        value={validation.values.configs[configIndex].details[detailIndex].value || ""} />
                                                      {validation.touched.configs?.[configIndex]?.details?.[detailIndex]?.value &&
                                                        validation.errors.configs?.[configIndex]?.details?.[detailIndex]?.value ?
                                                        (
                                                          <p className="text-red-400">
                                                            {validation.errors.configs?.[configIndex]?.details?.[detailIndex]?.value}
                                                          </p>
                                                        ) : null
                                                      }
                                                    </div>
                                                    <div className="xl:col-span-2">
                                                      <input type="number" id={`configDetailStock[${configIndex}][${detailIndex}]`} className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                                        placeholder="Stock"
                                                        name={`configs[${configIndex}].details[${detailIndex}].stock`}
                                                        onChange={validation.handleChange}
                                                        value={validation.values.configs[configIndex].details[detailIndex].stock || ""} />
                                                      {validation.touched.configs?.[configIndex]?.details?.[detailIndex]?.stock &&
                                                        validation.errors.configs?.[configIndex]?.details?.[detailIndex]?.stock ? (
                                                        <p className="text-red-400">{validation.errors.configs?.[configIndex]?.details?.[detailIndex]?.stock}</p>
                                                      ) : null}
                                                    </div>
                                                    <div className="xl:col-span-3">
                                                      <input type="number"
                                                        id={`configDetailPrice[${configIndex}][${detailIndex}]`}
                                                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                                        placeholder="Price (Retail)"
                                                        name={`configs[${configIndex}]details[${detailIndex}].price`}
                                                        onChange={validation.handleChange}
                                                        value={validation.values.configs[configIndex].details[detailIndex].price || ""} />
                                                      {validation.touched.configs?.[configIndex]?.details?.[detailIndex]?.price &&
                                                        validation.errors.configs?.[configIndex]?.details?.[detailIndex]?.price ? (
                                                        <p className="text-red-400">{validation.errors.configs?.[configIndex]?.details?.[detailIndex]?.price}</p>
                                                      ) : null}
                                                    </div>
                                                    <div className="xl:col-span-3">
                                                      <input type="number"
                                                        id={`configDetailPrice[${configIndex}][${detailIndex}]`}
                                                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                                        placeholder="Price (Wholesale)"
                                                        name={`configs[${configIndex}]details[${detailIndex}].wholeSalePrice`}
                                                        onChange={validation.handleChange}
                                                        value={validation.values.configs[configIndex].details[detailIndex].wholeSalePrice || ""} />
                                                      {validation.touched.configs?.[configIndex]?.details?.[detailIndex]?.wholeSalePrice &&
                                                        validation.errors.configs?.[configIndex]?.details?.[detailIndex]?.wholeSalePrice ? (
                                                        <p className="text-red-400">{validation.errors.configs?.[configIndex]?.details?.[detailIndex]?.wholeSalePrice}</p>
                                                      ) : null}
                                                    </div>
                                                    <div className="xl:col-span-1">
                                                      {
                                                        detailIndex === config.details?.length - 1 && detailIndex > 0 &&
                                                        <button type="button" className="btn btn-danger" onClick={() => {
                                                          const newConfigDetails = [...config.details];
                                                          newConfigDetails.splice(detailIndex, 1);
                                                          validation.setFieldValue(`configs[${configIndex}].details`, newConfigDetails);
                                                        }}><img src={deleteImg} alt="" className="block h-5 mx-auto" /></button>
                                                      }
                                                      {
                                                        detailIndex === config.details?.length - 1 &&
                                                        <button type="button" className="btn btn-primary" onClick={() => {
                                                          const newConfigDetails = [...config.details, { value: '', stock: '', price: '', image: '' }];
                                                          validation.setFieldValue(`configs[${configIndex}].details`, newConfigDetails);
                                                        }}><img src={addImg} alt="" className="block h-5 mx-auto" /></button>
                                                      }
                                                    </div>
                                                  </Fragment>
                                                )
                                              })
                                            }
                                          </div>
                                        )} />
                                      </div>
                                    }
                                    {
                                      validation.values.configs[configIndex].type === 'color' &&
                                      <div className="xl:col-span-12">
                                        <FieldArray name="configDetails" render={configDetailsArray => (
                                          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12">
                                            {
                                              config.details.map((detail: any, detailIndex: number) => {
                                                return (
                                                  <Fragment key={detailIndex}>
                                                    <div className="xl:col-span-4">
                                                      <Select
                                                        className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                                        options={availableColors}
                                                        isSearchable={false} // To disable search
                                                        name={`configs[${configIndex}]details[${detailIndex}].value`}
                                                        id={`configDetailColor[${configIndex}][${detailIndex}]`}
                                                        onChange={selectedOption => {
                                                          validation.setFieldValue(`configs[${configIndex}].details[${detailIndex}].value`, selectedOption?.value)
                                                        }}
                                                        value={availableColors.find(option => { return option.value === validation.values.configs[configIndex].details[detailIndex].value })}
                                                      />
                                                    </div>
                                                    <div className="xl:col-span-2">
                                                      <input type="number"
                                                        id={`configDetailStock[${configIndex}][${detailIndex}]`}
                                                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                                        placeholder="Stock"
                                                        name={`configs[${configIndex}].details[${detailIndex}].stock`}
                                                        onChange={validation.handleChange}
                                                        value={validation.values.configs[configIndex].details[detailIndex].stock || ""} />
                                                      {
                                                        validation.touched.configs?.[configIndex]?.details?.[detailIndex]?.stock &&
                                                          validation.errors.configs?.[configIndex]?.details?.[detailIndex]?.stock ? (
                                                          <p className="text-red-400">
                                                            {validation.errors.configs?.[configIndex]?.details?.[detailIndex]?.stock}
                                                          </p>
                                                        ) : null
                                                      }
                                                    </div>
                                                    {/* <div className="xl:col-span-4">
                                                      <input type="number"
                                                        id={`configDetailPrice[${configIndex}][${detailIndex}]`}
                                                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                                                        placeholder="Price"
                                                        name={`configs[${configIndex}]details[${detailIndex}].price`}
                                                        onChange={validation.handleChange}
                                                        value={validation.values.configs[configIndex].details[detailIndex].price || ""} />
                                                      {
                                                        validation.touched.configs?.[configIndex]?.details?.[detailIndex]?.price &&
                                                          validation.errors.configs?.[configIndex]?.details?.[detailIndex]?.price ? (
                                                          <p className="text-red-400">
                                                            {validation.errors.configs?.[configIndex]?.details?.[detailIndex]?.price}
                                                          </p>
                                                        ) : null
                                                      }
                                                    </div> */}
                                                    <div className="xl:col-span-2">
                                                      {
                                                        detailIndex === config.details?.length - 1 && detailIndex > 0 &&
                                                        <button type="button" className="btn btn-danger" onClick={() => {
                                                          const newConfigDetails = [...config.details];
                                                          newConfigDetails.splice(detailIndex, 1);
                                                          validation.setFieldValue(`configs[${configIndex}].details`, newConfigDetails);
                                                        }}><img src={deleteImg} alt="" className="block h-5 mx-auto" /></button>
                                                      }
                                                      {
                                                        detailIndex === config.details?.length - 1 &&
                                                        <button type="button" className="btn btn-primary" onClick={() => {
                                                          const newConfigDetails = [...config.details, { value: '', stock: '', price: '', image: '' }];
                                                          validation.setFieldValue(`configs[${configIndex}].details`, newConfigDetails);
                                                        }}><img src={addImg} alt="" className="block h-5 mx-auto" /></button>
                                                      }
                                                    </div>
                                                    <div className="xl:col-span-4">
                                                      <span></span>
                                                    </div>
                                                  </Fragment>
                                                )
                                              })
                                            }
                                          </div>
                                        )} />
                                      </div>
                                    }
                                  </div>
                                </Fragment>
                              )
                            })}
                        </div>
                      )}
                      />
                    </div>
                    {/* <div className="lg:col-span-2 xl:col-span-12">
                      <label htmlFor="productTag" className="inline-block mb-2 text-base font-medium">Product Tag</label>
                      <input className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" id="productTag" data-choices data-choices-text-unique-true type="text" value="Fashion, Clothes, Headphones" />
                    </div> */}
                  </div>

                  {/* uncomment to check what validation hit error */}
                  {/* {validation.touched && validation.errors ? (
                    <p className="text-red-400">{JSON.stringify(validation.errors)}</p>
                  ) : null} */}

                  <div className="flex justify-end gap-2 mt-4">
                    {/* <button type="reset" className="text-red-500 bg-white btn hover:text-red-500 hover:bg-red-100 focus:text-red-500 focus:bg-red-100 active:text-red-500 active:bg-red-100 dark:bg-zink-700 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10 dark:active:bg-red-500/10">Reset</button> */}
                    <button type="submit" className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20">{isUpdate ? 'Update Product' : 'Create Product'}</button>
                    {/* <button type="button" className="text-white bg-green-500 border-green-500 btn hover:text-white hover:bg-green-600 hover:border-green-600 focus:text-white focus:bg-green-600 focus:border-green-600 focus:ring focus:ring-green-100 active:text-white active:bg-green-600 active:border-green-600 active:ring active:ring-green-100 dark:ring-green-400/10">Draft & Preview</button> */}
                  </div>
                </form>
              </FormikProvider>
            </div>
          </div>
        </div>
        <div className="xl:col-span-3">
          <div className="card sticky top-[calc(theme('spacing.header')_*_1.3)]">
            <div className="card-body">
              <h6 className="mb-4 text-15">Product Card Preview</h6>

              <div className="px-5 py-8 rounded-md bg-sky-50 dark:bg-zink-600">
                {
                  isUpdate && (
                    <img src={getS3UrlFromFileId(validation.values.images[0], validation.values.files)} alt="" className="block mx-auto h-44" />
                  )
                }
                {
                  !isUpdate && selectfiles.length > 0 &&
                  <img src={selectfiles[0]?.preview} alt="" className="block mx-auto h-44" />
                }

              </div>

              <div className="mt-3">
                <h5 className="mb-2">{currencyFormatter(validation.values.price)}
                  {
                    validation.values.priceUnit && (
                      <span className="text-slate-500 dark:text-zink-200"> / {validation.values.priceUnit}</span>
                    )
                  }
                  {/* <small className="font-normal line-through">299.99</small> */}
                </h5>
                <h6 className="mb-1 text-15">{validation.values.name}</h6>
                <p className="text-slate-500 dark:text-zink-200">{validation.values.category?.value}</p>
              </div>
              {validation.values.specification && (
                <div className="mt-3">
                  <h6 className="mt-3 mb-2 text-15">Specification</h6>
                  {
                    validation.values.specification.map((spec: any, index: number) => (
                      spec.key &&
                      <p key={index} className="text-slate-500 dark:text-zink-200">{spec.key} : {spec.value}</p>
                    ))
                  }
                </div>
              )}
              {validation.values.length && (
                <div className="mt-3">
                  <h6 className="mt-3 mb-2 text-15">Length (meter)</h6>
                  <p className="text-slate-500 dark:text-zink-200">{validation.values.length}m</p>
                </div>
              )}

              {/* <div className="flex gap-2 mt-4">
                <button type="button" className="w-full bg-white border-dashed text-custom-500 btn border-custom-500 hover:text-custom-500 hover:bg-custom-50 hover:border-custom-600 focus:text-custom-600 focus:bg-custom-50 focus:border-custom-600 active:text-custom-600 active:bg-custom-50 active:border-custom-600 dark:bg-zink-700 dark:ring-custom-400/20 dark:hover:bg-custom-800/20 dark:focus:bg-custom-800/20 dark:active:bg-custom-800/20">Create Products</button>
                <button type="button" className="w-full text-white bg-purple-500 border-purple-500 btn hover:text-white hover:bg-purple-600 hover:border-purple-600 focus:text-white focus:bg-purple-600 focus:border-purple-600 focus:ring focus:ring-purple-100 active:text-white active:bg-purple-600 active:border-purple-600 active:ring active:ring-purple-100 dark:ring-purple-400/10">Draft</button>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default AddNew;